/*
 *******************************************************************************
 *
 *  Filename:   @/pages/system/Users/UserList.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Module for managing Users.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Friday, August 18, 2023 - 6:07 AM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/08/18  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import React, { useEffect, useState, useRef, useMemo } from "react";
import styled from "@emotion/styled";

import { fetchApi } from "@/utils/fetchApi";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { updateCompany, updateLocation, updateRole, updateUserStatus } from "@/redux/slices/users";

import {
  Box,
  Button,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Grid,
  Link as MuiLink,
  Paper as MuiPaper,
  Select,
  MenuItem,
  Typography as MuiTypography,
} from "@mui/material";

import { spacing } from "@mui/system";

import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS

import "@/App.css";

import AddIcon from "@mui/icons-material/Add";
import LoadingData from "@/pages/utils/LoadingData";
import SnackbarAlert from "@/pages/utils/SnackbarAlert";

import { useSelector } from "react-redux";

import DeleteDialog from "./DeleteDialog";
import { columns as getColumns } from "./Columns";
import UserEdit from "./UserEdit";

import awsSettings from "@/aws-exports";

LicenseManager.setLicenseKey(awsSettings.ag_grid_license);

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Link = styled(MuiLink)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Typography = styled(MuiTypography)(spacing);

function ItemDataGrid() {
  // Window Size
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 380);

  const gridRef = useRef(); // Optional - for accessing Grid's API

  // Grid Controls
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(100);

  // Form Dialogs
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
  }));

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
      position: "left",
    };
  }, []);

  // Other Settings
  const companyid = useSelector((state) => {
    return state.users.companyid;
  });
  const locationid = useSelector((state) => {
    return state.users.locationid;
  });
  const roleid = useSelector((state) => {
    return state.users.roleid;
  });
  const userstatusid = useSelector((state) => {
    return state.users.userstatusid;
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedRow, setSelectedRow] = useState(null);
  const [snackbarSettings, setSnackbarSettings] = useState({});
  const [openSnackbarAlert, setOpenSnackbarAlert] = useState(false);

  /*  state variable hooks */
  const [gridApi, setGridApi] = useState();

  // eslint-disable-next-line no-unused-vars
  const [columnApi, setColumnApi] = useState();

  const columnTypes = {
    dateColumn: {
      filter: "agDateColumnFilter",
      suppressMenu: true,
    },
  };

  const refetch = async () => {};

  const loading = null;

  const fetchData = async () => {
    try {
      const response = await fetchApi("users", "getUsers", {});
      setRows(response.rows);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchData();
    const handleResize = () => {
      setGridHeight(window.innerHeight - 380);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleAddClick = () => {
    setSelectedRow(null);
    setIsEditMode(false);
    setIsEditDialogOpen(true);
  };

  const handleEditClick = (event, item) => {
    setSelectedRow(item);
    setIsEditMode(true);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = (e, reason) => {
    if (reason === "escapeKeyDown" || reason === "backdropClick") {
      return false;
    }
    setIsEditDialogOpen(false);
    setSelectedRow({});
  };

  const handleDeleteClick = (event, item) => {
    setSelectedRow(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await fetchApi("users", "deleteUser", {
        userid: selectedRow.USERID,
      });
      fetchData();
      handleClickSnackbarAlert("success", "User has been successfully deleted.");
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleClickSnackbarAlert = (severity, message) => {
    setSnackbarSettings({
      severity: severity,
      message: message,
    });
    setOpenSnackbarAlert(true);
  };

  const handleCloseSnackbarAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarSettings({});
    setOpenSnackbarAlert(false);
  };

  const columns = getColumns(handleEditClick, handleDeleteClick);

  const onGridReady = (e) => {
    setGridApi(e.api);
    setColumnApi(e.columnApi);
    e.api.sizeColumnsToFit();
    e.columnApi.resetColumnState();
  };

  return (
    <Card mb={0}>
      <SnackbarAlert
        open={openSnackbarAlert}
        handleClose={handleCloseSnackbarAlert}
        message={snackbarSettings?.message}
        severity={snackbarSettings?.severity}
      />
      <UserEdit
        handleClose={handleCloseEditDialog}
        refetch={refetch}
        selectedRow={selectedRow}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        isEditDialogOpen={isEditDialogOpen}
        handleClickSnackbarAlert={handleClickSnackbarAlert}
      />
      <DeleteDialog open={isDeleteDialogOpen} onDelete={handleDelete} onClose={handleDeleteClose} />
      <CardContent pb={3}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item display="flex">
            <Box display="flex" alignItems="center" mr={3}>
              2 matches found.
            </Box>
          </Grid>
          <Grid item pb={2}>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddClick}>
              Add New
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      <Paper>
        <div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
          {loading ? (
            <LoadingData message="Fetching users..." minHeight={400} />
          ) : (
            <AgGridReact
              onGridReady={onGridReady}
              ref={gridRef}
              rowData={rows}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              columnTypes={columnTypes}
              animateRows={true}
              rowSelection="single"
              sideBar={sideBar}
            />
          )}
        </div>
      </Paper>
    </Card>
  );
}

function UserList() {
  return (
    <React.Fragment>
      <ItemDataGrid />
    </React.Fragment>
  );
}

export default UserList;
